/**
 * TrueMark — централизованный QueryClient с защитой от race-условий.
 *
 * Фундаментальная проблема: polling-запрос, стартовавший ДО мутации, может
 * завершиться ПОСЛЕ неё и перетереть кэш React Query устаревшим снимком.
 * Даже при `cancelQueries` в мутации есть окна (закешированная staleTime-выдача,
 * параллельные хуки, WebSocket-fallback), где старый снимок всё равно приходит.
 *
 * Решение: `structuralSharing`, который сравнивает `_version` у документов
 * (Task, Shipment и пр.). Если новый ответ имеет строго меньший `_version`,
 * чем то, что уже в кэше — возвращаем старые данные и логируем событие.
 *
 * `_version` инкрементится на бэке в `save_json_versioned` (services/storage.py#L239)
 * и проставляется в ответах через Task → to_dict().
 *
 * Побочный эффект — глобальное логирование ошибок мутаций.
 */

import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query'
import { logger } from '@/lib/logger'

/** Документ TrueMark с оптимистичной блокировкой. */
interface Versioned {
  _version?: number
  // Поля, которые позволяют идентифицировать ресурс в логе
  id?: string
}

function hasVersion(x: unknown): x is Versioned {
  return !!x && typeof x === 'object' && '_version' in (x as object)
}

/**
 * Version-aware structural sharing.
 *
 * Вызывается React Query для каждого успешного `queryFn` — между «что лежит в кэше»
 * (oldData) и «что вернул сервер» (newData). Если oldData версионнее, значит мы
 * получили просроченный снимок (polling, отправленный до мутации). Отбрасываем его.
 *
 * Важно: возвращаем `oldData` БЕЗ структурного шаринга — это значит React Query
 * не триггерит rerender подписчиков, что корректно: данные не поменялись.
 */
export function versionAwareStructuralSharing<T>(oldData: T | undefined, newData: T): T {
  if (!hasVersion(oldData) || !hasVersion(newData)) {
    return newData
  }
  const oldV = oldData._version
  const newV = newData._version
  if (typeof oldV === 'number' && typeof newV === 'number' && newV < oldV) {
    logger.warn(
      'stale-response-rejected',
      'Polling/fetch вернул устаревший _version — перетирание кэша предотвращено',
      {
        id: oldData.id ?? newData.id,
        oldVersion: oldV,
        newVersion: newV,
      },
    )
    return oldData
  }
  return newData
}

/** Создаёт QueryClient с глобальным логированием и stale-guard'ом. */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        // Не логируем отменённые запросы (cancelQueries / размонтирование)
        const anyErr = err as { name?: string; message?: string }
        if (anyErr?.name === 'AbortError' || anyErr?.name === 'CancelledError') return
        logger.warn('query-error', anyErr?.message || 'Query failed', {
          queryKey: query.queryKey,
        })
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, variables, _ctx, mutation) => {
        const anyErr = err as { name?: string; message?: string }
        logger.error('mutation-error', anyErr?.message || 'Mutation failed', {
          mutationKey: mutation.options.mutationKey,
          variables,
        })
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
        // Любой query-ответ с устаревшим `_version` будет отклонён автоматически.
        structuralSharing: versionAwareStructuralSharing,
      },
    },
  })
}
